import { IChartList } from "pages/meter/types";
import { useEffect, useState, useRef, memo } from "react";
import * as echarts from "echarts";
import { FormattedMessage } from "react-intl";


type IProps = {
  activeIndex: number;
  chartList: IChartList[];
  isMode: boolean;
  onChange: (index: number) => void;
};

export const BloodPressureEchart: React.FC<IProps> = memo(
  ({ activeIndex, chartList, isMode,onChange }) => {
    const [state, setState] = useState<{ sys: number; dia: number }>({
      sys: 0,
      dia: 0,
    });

    const echartPulDomRef = useRef<HTMLDivElement | null>(null);
    const echartDom = useRef<echarts.ECharts | null>(null);

    const setDataIndex = () => {
      if (echartDom.current) {
        echartDom.current.dispatchAction({
          type: "showTip",
          seriesIndex: 0,
          dataIndex: activeIndex,
        });
      }
    };

    useEffect(() => {
      if (chartList.length > 0) {
        setState(chartList[activeIndex]);
      }
    }, [activeIndex, chartList]);

    useEffect(() => {
      setDataIndex();
    }, [activeIndex]);

    const initializePulseChart = () => {
      if (!echartPulDomRef.current) return;
      const echartPulDom = echarts.init(echartPulDomRef.current);

      const clickHandler = (params: any) => {
        const { dataIndex } = params;
        onChange(dataIndex);
      };
      echartPulDom.on("click", clickHandler);
      return { echartPulDom, clickHandler };
    };

    const renderSysDiaChart = (echartPulDom: any) => {
      let xAxisData: string[] = [],
        sysData: number[] = [],
        diaData: number[] = [],
        otherData: number[] = [],
        sysResult: number[] = [];
      if (!chartList || chartList.length == 0) return;
      chartList.forEach((val, key) => {
        xAxisData.push(val.date as string);
        sysData.push(val.sys);
        sysResult.push(val.sys - val.dia);
        diaData.push(val.dia);
        otherData.push(0);
      });

      const option = {
        textStyle: {
          color: isMode ? "#f5f5f5" : "#333333",
          fontSize: 11,
        },
        grid: {
          top: 40,
          right: 15,
          left: 35,
          bottom: 40,
        },
        tooltip: {
          show: true,
          trigger: "axis", //  item
          triggerOn: "click",
          confine: false, //是否将 tooltip 框限制在图表的区域内
          alwaysShowContent: true,
          position: function (
            point: any,
            _: any,
            __: any,
            ___: any,
            size: any
          ) {
            const width = Math.ceil(size.contentSize[0] / 2);
            return [point[0] - width, -6];
          },
          backgroundColor: "#00C7AF",
          borderColor: "#00C7AF",
          padding: 6, //[6,8]
          textStyle: {
            color: "#fff",
            fontSize: 11,
            fontFamily: "Roboto",
          },
          axisPointer: {
            type: "line", //line //shadow
            axis: "x",
            lineStyle: {
              color: "#C4C4C4",
            },
          },
          formatter: (params: any) => {
            return params[0].axisValueLabel;
          },
        },
        xAxis: {
          data: xAxisData,
          axisLine: {
            lineStyle: {
              // X轴线
              color: "#E3E3E5",
            },
          },
          axisLabel: {
            lineHeight: 26,
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          // max: 300,
          splitNumber: 3,
          splitLine: {
            // 网格线
            show: true,
            lineStyle: {
              color: ["#E3E3E5"],
            },
          },
        },
        series: [
          // 第一部分 悬空bar(透明)
          {
            type: "bar",
            stack: "Total",
            barWidth: 8,
            data: diaData,
            name: "transparent",
            itemStyle: {
              color: "transparent",
            },
          },
          // 第二部分 黄色圆圈
          {
            type: "scatter",
            name: "scatter",
            // color: '#FFF629',
            stack: "Total",
            barWidth: 8,
            data: otherData,
            itemStyle: {
              opacity: 0.4,
              color: (params: any) => {
                return diaData[params.dataIndex] == 0 ? "#909192" : "#FFF629";
              },
            },
          },

          // 第三部分 柱状图 渐变背景+透明度
          {
            type: "bar",
            name: "SYS",
            stack: "Total",
            barWidth: 8,
            itemStyle: {
              normal: {
                opacity: 0.2,
                color: new echarts.graphic.LinearGradient(
                  0,
                  1,
                  0,
                  0,
                  [
                    {
                      offset: 0.2,
                      color: "#FFF629", // 0% 处的颜色
                    },
                    {
                      offset: 1,
                      color: "#00C7AF", // 100% 处的颜色
                    },
                  ],
                  false
                ),
              },
            },
            data: sysResult,
          },
          // 第四部分 最高位置的绿色圆圈
          {
            type: "scatter",
            name: "scatter",
            // color: '#00C7AF',
            stack: "Total",
            barWidth: 8,
            data: otherData,
            itemStyle: {
              opacity: 0.4,
              color: (params: any) => {
                return sysData[params.dataIndex] == 0 ? "#909192" : "#00C7AF";
              },
            },
          },
        ],
      };
      echartPulDom.setOption(option);

      return echartPulDom;
    };

    useEffect(() => {
      const chartInitResult = initializePulseChart();
      if (chartInitResult) {
        const { echartPulDom, clickHandler } = chartInitResult;

        if (echartPulDom) {
          renderSysDiaChart(echartPulDom);
          echartDom.current = echartPulDom;
          setDataIndex();
        }
        return () => {
          echartPulDom.off("click", clickHandler);
          echartPulDom.dispose();
        };
      }
    }, [chartList, echartPulDomRef.current]);

    return (
      <div className="flex flex-col items-center justify-center w-full md:w-[calc(100%-80px)] xl:max-w-[48%]">
        <div className="bg-white dark:bg-black333 rounded-[21px] w-full h-[380px] p-[11px] lg:rounded-[38px] lg:min-w-[600px] lg:h-[536px] lg:py-[21px] lg:px-[44px] lg:w-[calc(100%-88px)] xl:h-[580px]">
          <div className="font-bold text-xl md:text-2xl lg:text-[32px] leading-[24px] text-primary pl-[5px] lg:leading-[38px] lg:pl-[10px]">
            <FormattedMessage id="meter.share.bloodPressure" />
          </div>
          <div className="flex items-end justify-between pl-[5px] pb-3 lg:pl-[10px] xl:pb-5">
            <div className="flex mt-[13px] gap-[18px] lg:mt-[27px] lg:gap-[36px]">
              <div className="sys">
                <span className="text-gray999 dark:text-white60 text-[10px] lg:text-[20px] lg:leading-[23px]">
                  SYS
                </span>
                <div className="sys-data">
                  <span className="font-bold text-[24px] leading-[28px] text-black333 dark:text-white lg:text-[36px] lg:leading-[56px]">
                    {state?.sys || 0}
                  </span>
                  <span className="text-[11px] leading-[13px] text-gray999 dark:text-white60 inline-block ml-[5px] lg:text-[16px] lg:leading-[19px]">
                    mmHg
                  </span>
                </div>
              </div>
              <div className="sys">
                <span className="text-gray999 dark:text-white60 text-[10px] lg:text-[20px] lg:leading-[23px]">
                  DIA
                </span>
                <div className="sys-data">
                  <span className="font-bold text-[24px] leading-[28px] text-black333 dark:text-white lg:text-[36px] lg:leading-[56px]">
                    {state?.dia || 0}
                  </span>
                  <span className="text-[11px] leading-[13px] text-gray999 dark:text-white60 inline-block ml-[5px] lg:text-[16px] lg:leading-[19px">
                    mmHg
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-[10px] text-[11px] text-gray999 lg:gap-[20px] lg:text-[16px]">
              <div className="flex items-center gap-[5px]">
                <div className="w-[10px] h-[10px] rounded-full bg-primary"></div>
                <span>SYS</span>
              </div>
              <div className="flex items-center gap-[5px]">
                <div className="w-[10px] h-[10px] rounded-full bg-[#efe600]"></div>
                <span>DIA</span>
              </div>
            </div>
          </div>
          <div
            ref={echartPulDomRef}
            id="echart"
            className="box-border w-full h-[260px] xl:h-[380px]"
          ></div>
        </div>

        <div className="flex items-center justify-center flex-wrap mt-[20px] text-[12px] text-black333 dark:text-white80 gap-[10px] lg:mt-[60px] lg:text-[16px] lg:flex-row lg:gap-[20px]">
          <span>
            <FormattedMessage id="meter.share.bloodPressureNormalRange" />
          </span>
          <div className="flex items-center gap-[20px]">
            <div className="flex items-center gap-[8px]">
              <div className="w-[10px] h-[10px] rounded-full bg-primary"></div>
              <span>90-139 mmHg</span>
            </div>
            <div className="flex items-center gap-[8px]">
              <div className="w-[10px] h-[10px] rounded-full bg-[#efe600]"></div>
              <span>60-89 mmHg</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
